import { Injectable } from '@angular/core';
import { humanize, toTitleCase, transform } from '@alduino/humanizer/string';
import FileSaver from 'file-saver';

import { ICsvExportColumn } from '../../model';

@Injectable()
export class CsvDownloadService {
  constructor() {
  }

  /**
   * Download the given data as a CSV file.
   *
   * @param {Array<any>} data - The data to be exported as CSV.
   * @param {string} documentName - The name of the CSV file.
   * @param {ICsvExportColumn[]} [headers=[]] - Optional headers for the CSV file.
   * @return {boolean} - Returns true if the data was exported successfully, false otherwise.
   */
  downloadCsv(data: Array<any>, documentName: string, headers: ICsvExportColumn[] = []): boolean {
    const csvData = this.arrayToCsv(data, headers);
    const blob: Blob = this.createBlob(csvData);

    FileSaver.saveAs(blob, `${ documentName }.csv`);

    return true; // exported successfully
  }

  /**
   * Create a Blob object from the given CSV data.
   *
   * @param {string} csvData - The CSV data to be converted into Blob.
   * @return {Blob} - A Blob object representing the CSV data.
   */
  private createBlob(csvData: string): Blob {
    const BOM: string = '\uFEFF'; // UTF-8 Byte Order Mark (BOM)
    return new Blob([ BOM + csvData ], { type: 'text/csv;charset=utf-8;' });
  }

  /**
   * Maps the headers from a JSON object to an array of ICsvExportColumn objects.
   * @param {any} obj - The JSON object containing the headers.
   * @return {ICsvExportColumn[]} - An array of ICsvExportColumn objects representing the headers.
   */
  private mapHeadersFromJson(obj: any): ICsvExportColumn[] {
    return Object.keys(obj).map(field => ({
      label: '',
      field: field,
    }));
  }

  /**
   * Maps the header of a CSV export column.
   *
   * @param {ICsvExportColumn} column - The column to map the header for.
   * @returns {ICsvExportColumnInternal} - The mapped column header.
   */
  private mapHeader(column: ICsvExportColumn): ICsvExportColumnInternal {
    const label: string = column.label || transform(humanize(column.field), toTitleCase);

    return {
      label: label,
      field: column.field,
    };
  }

  /**
   * Converts an array of JSON objects into a CSV string.
   *
   * @param {any[]} data - The array of JSON objects to convert.
   * @param {ICsvExportColumn[]} headers - The array of column headers to use in the CSV.
   * @returns {string} - The generated CSV string.
   */
  private arrayToCsv(data: any[], headers: ICsvExportColumn[]): string {
    if (headers.length === 0) {
      headers = this.mapHeadersFromJson(data[0]);
    }

    const mappedHeader = headers.map(header =>
      this.mapHeader(header));

    const mappedHeaderCsv = mappedHeader.map(header =>
      this.escapeField(header.label)).join(',');

    // Map JSON objects to CSV rows
    const csvRows = data.map(row => {
      return headers.map(header => {
        const fieldValue = row[header.field];

        if (fieldValue == null) {
          return '';
        }

        return this.escapeField(fieldValue.toString());
      }).join(',');
    });

    return [ mappedHeaderCsv, ...csvRows ].join('\n');
  }

  /**
   * Escapes a given field if it contains certain characters.
   *
   * @param {string} field - The field to be escaped.
   * @returns {string} - The escaped field.
   */
  private escapeField(field: string): string {
    if (field.includes(',') || field.includes('\n') || field.includes('"')) {
      return `"${ field.replace(/"/g, '""') }"`;
    }
    return field;
  }
}

/**
 * Represents an internal CSV export column.
 * @interface
 * @extends ICsvExportColumn
 */
interface ICsvExportColumnInternal extends ICsvExportColumn {
  label: string;
  field: string;
}
